/**
 * V70-N — 게임대회 모달을 사이드바를 제외한 본문(콘텐츠) 영역 중앙에 놓기 위한 좌우 여백 측정.
 *
 * `position: fixed; inset: 0` 오버레이는 뷰포트(사이드바 포함 전체 화면) 기준으로 가운데 정렬된다.
 * AppShell 은 사이드바(position: fixed, 280px) + 본문 wrapper(`data-app-shell-main="1"`, marginLeft 280px) 구조이므로
 * 본문 wrapper 의 실제 getBoundingClientRect() 를 재서 오버레이 좌우 padding 에 더해 준다.
 *   - 고정 픽셀 오프셋이 아니라 실제 레이아웃 측정값 (사이드바 폭이 바뀌면 자동 반영)
 *   - 본문 폭이 너무 좁으면(모바일: 사이드바가 화면 대부분을 차지) 화면 전체 기준으로 되돌린다
 *   - AppShell 밖(식별자 없음): 0 → 기존과 같은 뷰포트 중앙
 *   - 오버레이 자체는 계속 전체 화면을 덮는다 (바깥 클릭 닫기·딤 처리 그대로)
 * 첫 렌더 전에 측정(lazy init)하여 열 때 위치가 튀지 않게 하고, 창 크기·본문 크기 변화 시 다시 잰다.
 * 마블 결과 팝업(MarbleEventModals.useAppShellMainBounds)과 같은 측정 방식.
 */
import React, { useLayoutEffect, useState } from 'react';

export const APP_SHELL_MAIN_SELECTOR = '[data-app-shell-main="1"]';
/** 본문 영역이 이 폭보다 좁으면 화면 전체 기준으로 정렬 (390·360 모바일에서 본문은 110·80px) */
export const MIN_CONTENT_CENTER_WIDTH = 480;

export interface ContentInsets { left: number; right: number }

export function measureAppShellContentInsets(): ContentInsets {
  if (typeof document === 'undefined') return { left: 0, right: 0 };
  const el = document.querySelector<HTMLElement>(APP_SHELL_MAIN_SELECTOR);
  if (!el) return { left: 0, right: 0 };
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const left = Math.max(0, r.left);
  const right = Math.max(0, vw - r.right);
  if (vw - left - right < MIN_CONTENT_CENTER_WIDTH) return { left: 0, right: 0 };
  return { left, right };
}

export function useAppShellContentInsets(): ContentInsets {
  const [insets, setInsets] = useState<ContentInsets>(measureAppShellContentInsets);
  useLayoutEffect(() => {
    const measure = () => {
      const next = measureAppShellContentInsets();
      setInsets((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const el = document.querySelector<HTMLElement>(APP_SHELL_MAIN_SELECTOR);
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (el && ro) ro.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);
  return insets;
}

/** 오버레이 기본 스타일(padding 숫자)에 본문 영역 좌우 여백을 더해 flex 중앙이 본문 영역 중앙이 되게 한다 */
export function contentCenteredOverlay(base: React.CSSProperties, insets: ContentInsets): React.CSSProperties {
  const { padding, ...rest } = base;
  const pad = typeof padding === 'number' ? padding : 20;
  // shorthand(padding)와 longhand 를 섞지 않음 (React 재렌더 경고 방지)
  return { ...rest, paddingTop: pad, paddingBottom: pad, paddingLeft: pad + insets.left, paddingRight: pad + insets.right };
}
